/* eslint-disable jsx-a11y/label-has-associated-control */
import React, {
  useState,
  FormEvent,
  createContext,
  PropsWithChildren,
  FunctionComponent,
  useContext,
  ReactElement,
} from 'react'
import {
  StripeProvider,
  injectStripe,
  Elements,
  ReactStripeElements,
} from 'react-stripe-elements'
import { noop } from 'lodash'

import { useDidMount } from 'hooks'

import { getStripe } from './lib/stripe'

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type placeholderRenderFunction = () => ReactElement<any, any> | null

interface StripeCardFormProps {
  onSubscribe: (
    props: ReactStripeElements.TokenResponse
  ) => Promise<void | Error>
  onSubscribeStart: () => void
  className: string
  renderPlaceholder: placeholderRenderFunction
}

interface PaymentInputProps {
  onSubscribe: (
    props: ReactStripeElements.TokenResponse
  ) => Promise<void | Error>
  onSubscribeStart?: () => void
  className?: string
  renderPlaceholder?: placeholderRenderFunction
}

interface StripeCCFormStateContextInterface {
  isSubmitting: boolean
  isValid: boolean | undefined
  setIsValid: (isValid: boolean) => void
}

const StripeCCFormStateContext = createContext<
  StripeCCFormStateContextInterface
>({
  isSubmitting: false,
  isValid: false,
  setIsValid: noop,
})

export const useStripeCCFormStateContext = () =>
  useContext(StripeCCFormStateContext)

const renderPlaceholderDefault = () => null

const StripeCardForm = injectStripe(
  ({
    stripe,
    onSubscribe,
    children,
    onSubscribeStart,
    className,
    renderPlaceholder,
  }: PropsWithChildren<StripeCardFormProps> &
    ReactStripeElements.InjectedStripeProps) => {
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [isValid, setIsValid] = useState<boolean | undefined>()

    const handleSubmit = async (e: FormEvent) => {
      e.preventDefault()
      if (isSubmitting || !stripe) return
      setIsSubmitting(true)

      onSubscribeStart()

      const response = await stripe.createToken({ type: 'card' })
      await onSubscribe(response)
      setIsSubmitting(false)
    }

    if (!stripe) return renderPlaceholder()

    return (
      <form className={className} onSubmit={handleSubmit}>
        <StripeCCFormStateContext.Provider
          value={{ isSubmitting, isValid, setIsValid }}
        >
          {children}
        </StripeCCFormStateContext.Provider>
      </form>
    )
  }
)

const StripePaymentForm: FunctionComponent<PaymentInputProps> = ({
  children,
  onSubscribe,
  onSubscribeStart = noop,
  className = '',
  renderPlaceholder = renderPlaceholderDefault,
}) => {
  const [stripe, setStripe] = useState<stripe.Stripe | null>(null)

  const loadStripe = async () => {
    const stripe = await getStripe()
    if (stripe) {
      setStripe(stripe)
    }
  }

  useDidMount(loadStripe)

  return (
    <StripeProvider stripe={stripe}>
      <Elements>
        <StripeCardForm
          onSubscribeStart={onSubscribeStart}
          onSubscribe={onSubscribe}
          className={className}
          renderPlaceholder={renderPlaceholder}
        >
          {children}
        </StripeCardForm>
      </Elements>
    </StripeProvider>
  )
}

export default StripePaymentForm
